import { useAppSelector } from "../hooks";

/**
 * Shared, single frozen fallback so every empty read returns the SAME object
 * reference — putting the fallback here (not `|| {}` inside a selector) is what
 * stops a fresh `{}` per render from churning `useSelector`.
 */
const EMPTY = Object.freeze({});

/**
 * Shared read of the current site (SCRUM-1028, the `useCurrentPermission`
 * style).
 *
 * The slice used to keep TWO fields for this one concept: `site` and
 * `currentsite`. `currentSiteReceived` assigned the same payload to both, so
 * they agreed on the site's identity — but `addPage` / `deletePage` mutated
 * the site's `pages` and `required_pages` lists in place and never touched
 * `currentsite` (the `pages` list left the site in SCRUM-1812), so the copy went stale on exactly those fields. Two names for
 * one thing, disagreeing only where nobody happened to look.
 *
 * `currentsite` is gone; this hook is the read. Measured before removing it:
 * the three production readers used `.country_code`, `.design._id` and
 * `._id` / `.name` — identity and config only, never `.pages` or
 * `.required_pages`. So collapsing onto `site` changed no screen's behaviour.
 *
 * Returns the current site, or a frozen `{}` when none.
 */
export function useCurrentSite(): Record<string, any> {
  return (useAppSelector((s) => s.sites?.site) || EMPTY) as Record<string, any>;
}

